import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { reportRenderError } from '@/lib/crash-capture';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { EmptyState } from '@/ui/States';

/**
 * Catches a page that throws while rendering, so the shell around it - the
 * sidebar, the way to another page - keeps working. The layout keys it by
 * path, so navigating away gives the next page a fresh start. On PM pages the
 * crash is reported; the server has no other way to learn of it.
 */
export class CrashBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportRenderError(error, info.componentStack);
  }

  render() {
    return this.state.failed ? <PageCrashed /> : this.props.children;
  }
}

function PageCrashed() {
  const { t } = useTranslation();
  return (
    <Card>
      <EmptyState
        icon={<AlertTriangle className="size-5" aria-hidden />}
        title={t('errors.pageCrashedTitle')}
        hint={t('errors.pageCrashedBody')}
        action={<Button variant="outline" size="sm" onClick={() => window.location.reload()}>{t('common.reload')}</Button>}
      />
    </Card>
  );
}
